// Instagram-style paid verified badge — six-bumped blue scallop with a
// white tick. Renders only when `verified` is truthy; returns null
// otherwise so the parent JSX can always include it unconditionally.
//
// Sized at `0.9em` by default — 10% smaller than the surrounding text
// so it reads as a satellite mark, not a peer. Scales with parent
// font-size automatically. Override via the `size` prop (number → px,
// string → used directly, e.g. "1.2em" or "16px"). Anything that's not
// a number AND not a valid CSS length will be ignored thanks to the
// inline width/height style fallback. Place AFTER the user's real
// name — never next to a @username.

import React from 'react';

// CSS length tokens — every string size must match this to be honored.
// Anything else (e.g. "sm", "large", undefined garbage) falls through
// to the 0.9em default. Keeps stale call sites from blowing up the
// badge to fill the container, which is what was happening before.
const VALID_LENGTH = /^\d*\.?\d+(?:px|em|rem|%|vh|vw|ch)$/i;

export default function VerifiedBadge({ verified, size, className = '', title = 'Verified' }) {
  if (!verified) return null;
  let px = '0.9em';
  if (typeof size === 'number' && Number.isFinite(size)) px = `${size}px`;
  else if (typeof size === 'string' && VALID_LENGTH.test(size.trim())) px = size.trim();
  return (
    <svg
      className={`verified-badge ${className}`.trim()}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      role="img"
      aria-label={title}
      style={{
        display: 'inline-block',
        verticalAlign: '-0.12em',
        marginLeft: '0.25em',
        flexShrink: 0,
        // Belt and suspenders: re-assert dimensions in inline style so
        // a parent flex/grid context can't stretch the SVG to fill.
        width: px,
        height: px,
      }}
    >
      <title>{title}</title>
      <path
        d="M12 1.6l2.3 1.6 2.8-.3 1.5 2.4 2.5 1.3-.5 2.8 1.4 2.4-1.4 2.4.5 2.8-2.5 1.3-1.5 2.4-2.8-.3L12 22.4l-2.3-1.6-2.8.3-1.5-2.4-2.5-1.3.5-2.8L1.6 12 3 9.6l-.5-2.8 2.5-1.3 1.5-2.4 2.8.3z"
        fill="#3b82f6"
      />
      <path
        d="M10.6 15.6l-3-3 1.4-1.4 1.6 1.6 4.4-4.4 1.4 1.4z"
        fill="#fff"
      />
    </svg>
  );
}
